import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { deleteRestaurant } from '../../services/restaurant/restaurantService';
import { updateSubscriptionQuantity } from '../../services/subscription/subscriptionService';
import { useAuth } from '../../services/authentication/authContext';
import { hasFreeSubscription } from '../../services/accessManagement/accessManagement';

interface DeleteRestaurantModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  restaurantName: string;
  onDelete: () => Promise<void>;
}

export default function DeleteRestaurantModal({
  isOpen,
  onClose,
  restaurantId,
  restaurantName,
  onDelete,
}: DeleteRestaurantModalProps) {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if this admin user is in the free subscription list
  const isFreeSubscriptionUser = hasFreeSubscription(backendUser, accountSettings);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);

    try {
      // Only update subscription quantity if user is not in free subscription list
      if (!isFreeSubscriptionUser) {
        try {
          await updateSubscriptionQuantity(-1);
        } catch (subscriptionErr: any) {
          console.error('Failed to update subscription:', subscriptionErr);
          setError(
            subscriptionErr.message || 'Failed to update subscription. Please try again.'
          );
          setDeleting(false);
          return; // Don't proceed with restaurant deletion if subscription update fails
        }
      }

      // If subscription update succeeded (or was skipped for free users), delete the restaurant
      await deleteRestaurant(restaurantId);
      await onDelete();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError('Failed to delete restaurant');
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {t('restaurantDetail.deleteRestaurantTitle')}
            </h2>
            <button
              onClick={onClose}
              disabled={deleting}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-50"
              aria-label={t('restaurantDetail.close')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border border-red-100 dark:border-red-900/30">
              <div className="flex gap-3">
                <div className="flex-none mt-0.5">
                  <div className="h-8 w-8 rounded-lg bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-500 grid place-items-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-zinc-900 dark:text-zinc-100 font-medium mb-1">{t('restaurantDetail.cannotBeUndone')}</p>
                  <p className="text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed">
                    {t('restaurantDetail.deleteConfirmation', { name: restaurantName })}
                  </p>
                </div>
              </div>
            </div>

            {/* Subscription info (only for non-free users) */}
            {!isFreeSubscriptionUser && (
              <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      Subscription Update
                    </h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Removing this restaurant will automatically decrease your subscription quantity by 1.
                      You'll receive a prorated credit applied to your next invoice.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={deleting}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
            >
              {t('restaurantDetail.cancel')}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-red-600 dark:bg-red-700 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? 'Deleting...' : t('restaurantDetail.deleteRestaurantButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
